import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import type { MonetaryAmount } from '@sorare/core/src/lib/monetaryAmount';

import type { useManagerSalesTag_anyCard } from './__generated__/useManagerSalesTag.graphql.ts';

type Props = {
  anyCards?: useManagerSalesTag_anyCard[];
};

export const useManagerSalesTag = ({ anyCards = [] }: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const bestPrice = anyCards.reduce<{
    slug: string | null;
    price: MonetaryAmount | null;
  }>(
    (acc, { slug, liveSingleSaleOffer }) => {
      const { price } = acc;
      const currentPrice =
        (liveSingleSaleOffer?.receiverSide.amounts &&
          toMonetaryAmount(liveSingleSaleOffer?.receiverSide.amounts)) ||
        null;

      if (currentPrice && (!price || currentPrice.eurCents < price.eurCents))
        return {
          slug,
          price: currentPrice,
        };
      return acc;
    },
    { slug: null, price: null }
  );

  const highestLevel = anyCards.reduce<{
    slug: string | null;
    grade: number;
  }>(
    (acc, anyCard) => {
      const { grade } = acc;
      const currentGrade = anyCard.grade || 0;
      if (currentGrade > grade)
        return {
          ...anyCard,
        };
      return acc;
    },
    { slug: null, grade: 0 }
  )?.slug;

  return {
    bestPrice,
    highestLevel,
  };
};

useManagerSalesTag.fragments = {
  anyCard: gql`
    fragment useManagerSalesTag_anyCard on AnyCardInterface {
      slug
      liveSingleSaleOffer {
        id
        receiverSide {
          id
          amounts {
            ...useMonetaryAmount_monetaryAmount
          }
        }
      }
      grade
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<useManagerSalesTag_anyCard>,
};
